import { Injectable } from "@angular/core";
import { TabSdk } from "@techextensor/tab-sdk";
import { isEmpty } from "lodash";
import moment from "moment";
import { FormioUtils } from "@formio/angular";

@Injectable({
    providedIn: 'root'
})
export class FavFilterService {

    /**
     * Main function to handle filter events and broadcast transformed data
     */
    public processFilter(filterData: any): void {
        const transformedData = this.transformFilterDataToRequestParams(filterData);
        TabSdk.util.broadcastEvent('onGlobalFilter', transformedData);
    }

    /**
     * Extract and transform form data to filter data structure
     * Converts form's componentValue format to flat key-value pairs
     */
    public transformFilterDataToRequestParams(filterData: any): any {
        const processedEntries = Object.entries(filterData ?? {})
            .map(([key, value]: any) => {
                if (value?.component || value?.componentValue) {
                    const component = value.component;

                    if (component?.type === 'DateRangePicker') {
                        const dateValue = value?.componentValue;
                        const startDate = dateValue?.length ? dateValue[0] : dateValue?.start;
                        const endDate = dateValue?.length ? dateValue[1] : dateValue?.end;

                        const formattedStartDate = startDate ? moment(startDate).format('YYYY-MM-DD') : '';
                        const formattedEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : '';

                        // Get custom keys or defaults
                        const baseStartKey = component?.Configuration?.componentValue?.startDateKey || 'startDate';
                        const baseEndKey = component?.Configuration?.componentValue?.endDateKey || 'endDate';

                        // Extract parent path from component key
                        // Example: "columns.DATE" → parent = "columns"
                        // Example: "DATE" → parent = ""
                        const keyParts = key.split('.');
                        const parentPath = keyParts.slice(0, -1).join('.');

                        // Build keys at same level as component
                        const startDateKey = parentPath ? `${parentPath}.${baseStartKey}` : baseStartKey;
                        const endDateKey = parentPath ? `${parentPath}.${baseEndKey}` : baseEndKey;

                        return [
                            [startDateKey, formattedStartDate],
                            [endDateKey, formattedEndDate]
                        ];
                    } else {
                        return [[key, value?.componentValue ?? '']];
                    }

                } else if (Array.isArray(value)) {
                    return [[key, value.filter((item: any) => item?.length).join(',')]];

                } else if (typeof value === 'object' && isEmpty(value)) {
                    return [[key, '']];

                } else {
                    return [[key, value]];
                }
            })
            .flat();

        const transformedData = Object.fromEntries(processedEntries);
        delete transformedData?.applyFilter;
        return transformedData;
    }

    /**
     * Transform filter data back to form data structure for loading values
     */
    public transformRequestParamsToFilterData(form: any, filterData: any): any {
        const formData: any = { ...filterData }; // Start with copy of filter data

        // Flatten components once
        const flatComponents = FormioUtils.flattenComponents(form?.components ?? [], true);

        // Iterate through components and handle special cases
        Object.entries(flatComponents).forEach(([componentKey, component]: [string, any]) => {
            if (component.type === 'DateRangePicker') {
                // Get custom keys or defaults
                const baseStartKey = component?.Configuration?.componentValue?.startDateKey || 'startDate';
                const baseEndKey = component?.Configuration?.componentValue?.endDateKey || 'endDate';

                // Extract parent path
                const keyParts = componentKey.split('.');
                const parentPath = keyParts.slice(0, -1).join('.');

                // Build keys at same level as component
                const startDateKey = parentPath ? `${parentPath}.${baseStartKey}` : baseStartKey;
                const endDateKey = parentPath ? `${parentPath}.${baseEndKey}` : baseEndKey;

                const startDate = filterData[startDateKey] || '';
                const endDate = filterData[endDateKey] || '';

                let componentValue = {};
                if (startDate?.length || endDate?.length) {
                    componentValue = [startDate, endDate];
                }

                // Add date component data
                formData[componentKey] = {
                    component: component,
                    componentValue: componentValue
                }

                // Remove start/end date keys from formData
                delete formData[startDateKey];
                delete formData[endDateKey];

            } else if (component.type === 'select' && component.multiple) {
                // Handle multi-select
                const value = filterData[componentKey];
                if (value !== undefined) {
                    formData[componentKey] = typeof value === 'string' ? value.split(',') : value;
                }
            }
        });

        return formData;
    }
}